import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogOut, FileText, Users, MessageSquare, Calendar, Trash2, Plus, X } from "lucide-react";

const TABS = [
  { id: "appointments", label: "Appointments", icon: Calendar },
  { id: "blogs", label: "Blogs", icon: FileText },
  { id: "testimonials", label: "Testimonials", icon: Users },
  { id: "contacts", label: "Messages", icon: MessageSquare },
];

export default function AdminDashboard() {
  const { user, logout, authHeaders } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("appointments");

  if (user === null) return <div className="p-10 text-center text-slate-500">Loading...</div>;
  if (user === false || !user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-dashboard">
      <header className="bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="https://customer-assets.emergentagent.com/job_healing-connect-30/artifacts/merdnxy1_ChatGPT%20Image%20Jun%2018%2C%202026%2C%2012_57_26%20PM%20%281%29.png" alt="logo" className="w-10 h-10 rounded-xl object-cover" />
            <div>
              <div className="font-serif text-lg text-slate-900">Admin Dashboard</div>
              <div className="text-xs text-slate-500">{user.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => nav("/")} className="text-sm text-slate-600 hover:text-green-700">View site</button>
            <button onClick={async () => { await logout(); nav("/"); }} data-testid="admin-logout-btn" className="inline-flex items-center gap-1.5 bg-slate-900 text-white rounded-full px-4 py-2 text-sm">
              <LogOut className="w-3.5 h-3.5" /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-2 mb-7 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium whitespace-nowrap transition ${
                tab === t.id ? "bg-green-700 text-white shadow" : "bg-white border border-slate-200 text-slate-700 hover:border-green-500 hover:text-green-700"
              }`}
            >
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "appointments" && <AppointmentsTab authHeaders={authHeaders} />}
        {tab === "blogs" && <BlogsTab authHeaders={authHeaders} />}
        {tab === "testimonials" && <TestimonialsTab authHeaders={authHeaders} />}
        {tab === "contacts" && <ContactsTab authHeaders={authHeaders} />}
      </div>
    </div>
  );
}

function AppointmentsTab({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/appointments", { headers: authHeaders });
      setItems(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/appointments/${id}?status=${status}`, null, { headers: authHeaders });
      toast.success("Status updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (loading) return <div className="text-center text-slate-500 py-10">Loading...</div>;

  return (
    <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden" data-testid="admin-appointments-list">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
        <h2 className="font-serif text-2xl text-slate-900">Appointment Requests</h2>
        <span className="text-sm text-slate-500">{items.length} total</span>
      </div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-slate-500">No requests yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Phone</th>
                <th className="px-5 py-3">Condition</th>
                <th className="px-5 py-3">Date</th>
                <th className="px-5 py-3">Status</th>
                <th className="px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((a) => (
                <tr key={a.id} className="border-t border-slate-100" data-testid={`appt-row-${a.id}`}>
                  <td className="px-5 py-3 font-medium text-slate-900">{a.name}</td>
                  <td className="px-5 py-3"><a href={`tel:${a.phone}`} className="text-green-700">{a.phone}</a></td>
                  <td className="px-5 py-3 text-slate-600">{a.condition || "—"}</td>
                  <td className="px-5 py-3 text-slate-600">{a.preferred_date || "—"}</td>
                  <td className="px-5 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs ${
                      a.status === "new" ? "bg-blue-50 text-blue-700" : a.status === "contacted" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
                    }`}>{a.status}</span>
                  </td>
                  <td className="px-5 py-3">
                    <select value={a.status} onChange={(e) => updateStatus(a.id, e.target.value)} className="border border-slate-200 rounded-lg px-2 py-1.5 text-xs">
                      <option value="new">New</option>
                      <option value="contacted">Contacted</option>
                      <option value="completed">Completed</option>
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ContactsTab({ authHeaders }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get("/admin/contacts", { headers: authHeaders }).then((r) => setItems(r.data)).catch(() => {});
  }, []);
  return (
    <div className="bg-white rounded-2xl border border-slate-100" data-testid="admin-contacts-list">
      <div className="px-5 py-4 border-b border-slate-100"><h2 className="font-serif text-2xl text-slate-900">Contact Messages</h2></div>
      {items.length === 0 ? (
        <div className="p-10 text-center text-slate-500">No messages yet.</div>
      ) : (
        <div className="divide-y divide-slate-100">
          {items.map((c) => (
            <div key={c.id} className="p-5">
              <div className="flex justify-between gap-4">
                <div>
                  <div className="font-medium text-slate-900">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{c.phone} • {c.email || "no email"}</div>
                </div>
                <div className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString()}</div>
              </div>
              <p className="text-slate-700 mt-3 text-sm">{c.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BlogsTab({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/blogs", { headers: authHeaders }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this blog?")) return;
    try {
      await api.delete(`/admin/blogs/${id}`, { headers: authHeaders });
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="font-serif text-2xl text-slate-900">Blog Posts</h2>
        <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-blog-btn" className="inline-flex items-center gap-1.5 bg-green-700 text-white rounded-full px-4 py-2 text-sm font-medium">
          <Plus className="w-4 h-4" /> New blog
        </button>
      </div>
      <div className="bg-white rounded-2xl border border-slate-100 divide-y divide-slate-100" data-testid="admin-blogs-list">
        {items.length === 0 ? (
          <div className="p-10 text-center text-slate-500">No blogs yet.</div>
        ) : (
          items.map((b) => (
            <div key={b.id} className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
              {b.cover_image && <img src={b.cover_image} alt="" className="w-24 h-16 object-cover rounded-lg" />}
              <div className="flex-1">
                <div className="font-medium text-slate-900">{b.title}</div>
                <div className="text-xs text-slate-500 mt-0.5">{b.category} • {b.published ? "Published" : "Draft"} • /{b.slug}</div>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { setEditing(b); setShowForm(true); }} className="text-sm text-green-700 px-3 py-1.5 rounded-lg hover:bg-green-50">Edit</button>
                <button onClick={() => remove(b.id)} data-testid={`delete-blog-${b.id}`} className="text-sm text-red-600 px-3 py-1.5 rounded-lg hover:bg-red-50 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
          ))
        )}
      </div>
      {showForm && (
        <BlogFormModal
          authHeaders={authHeaders}
          initial={editing}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function BlogFormModal({ authHeaders, initial, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: initial?.title || "",
    excerpt: initial?.excerpt || "",
    content: initial?.content || "",
    category: initial?.category || "General",
    cover_image: initial?.cover_image || "",
    author: initial?.author || "Dr. Ashutosh Mishra",
    read_time: initial?.read_time || "5 min read",
    published: initial?.published ?? true,
  });
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (initial) {
        await api.put(`/admin/blogs/${initial.id}`, form, { headers: authHeaders });
      } else {
        await api.post("/admin/blogs", form, { headers: authHeaders });
      }
      toast.success(initial ? "Blog updated" : "Blog published");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4 overflow-auto">
      <form onSubmit={save} className="bg-white rounded-3xl p-7 w-full max-w-2xl my-10" data-testid="blog-form-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-2xl text-slate-900">{initial ? "Edit blog" : "New blog post"}</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5 text-slate-500" /></button>
        </div>
        <div className="space-y-3">
          <input required placeholder="Title" value={form.title} onChange={update("title")} data-testid="blog-title" className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <input placeholder="Category (e.g. Diabetes)" value={form.category} onChange={update("category")} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <input placeholder="Cover image URL" value={form.cover_image} onChange={update("cover_image")} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <textarea required placeholder="Short excerpt (1-2 sentences)" value={form.excerpt} onChange={update("excerpt")} rows={2} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <textarea required placeholder="Full HTML content (use <h2>, <p>, <ul><li>...)" value={form.content} onChange={update("content")} rows={10} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm font-mono" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Author" value={form.author} onChange={update("author")} className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
            <input placeholder="Read time" value={form.read_time} onChange={update("read_time")} className="px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input type="checkbox" checked={form.published} onChange={update("published")} /> Published
          </label>
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm text-slate-700">Cancel</button>
          <button disabled={saving} data-testid="save-blog-btn" className="bg-green-700 text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60">
            {saving ? "Saving..." : initial ? "Update" : "Publish"}
          </button>
        </div>
      </form>
    </div>
  );
}

function TestimonialsTab({ authHeaders }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/admin/testimonials", { headers: authHeaders }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`, { headers: authHeaders });
    toast.success("Deleted");
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex justify-between items-center">
        <h2 className="font-serif text-2xl text-slate-900">Testimonials</h2>
        <button onClick={() => setShowForm(true)} data-testid="add-testimonial-btn" className="inline-flex items-center gap-1.5 bg-green-700 text-white rounded-full px-4 py-2 text-sm font-medium">
          <Plus className="w-4 h-4" /> Add
        </button>
      </div>
      <div className="grid md:grid-cols-2 gap-4" data-testid="admin-testimonials-list">
        {items.map((t) => (
          <div key={t.id} className="bg-white border border-slate-100 rounded-2xl p-5">
            <div className="flex justify-between gap-3">
              <div>
                <div className="font-medium text-slate-900">{t.name}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t.condition} • {t.location} • {t.rating}★</div>
              </div>
              <button onClick={() => remove(t.id)} data-testid={`delete-testimonial-${t.id}`} className="text-red-600"><Trash2 className="w-4 h-4" /></button>
            </div>
            <p className="text-sm text-slate-700 mt-3">"{t.quote}"</p>
          </div>
        ))}
      </div>
      {showForm && (
        <TestimonialFormModal
          authHeaders={authHeaders}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); load(); }}
        />
      )}
    </div>
  );
}

function TestimonialFormModal({ authHeaders, onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", condition: "", rating: 5, quote: "", location: "Varanasi" });
  const [saving, setSaving] = useState(false);
  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: k === "rating" ? Number(e.target.value) : e.target.value }));
  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/testimonials", form, { headers: authHeaders });
      toast.success("Added");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 grid place-items-center p-4">
      <form onSubmit={save} className="bg-white rounded-3xl p-7 w-full max-w-md" data-testid="testimonial-form-modal">
        <div className="flex justify-between items-center mb-6">
          <h3 className="font-serif text-2xl text-slate-900">Add testimonial</h3>
          <button type="button" onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-3">
          <input required placeholder="Patient name" value={form.name} onChange={update("name")} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <input placeholder="Condition treated" value={form.condition} onChange={update("condition")} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <input placeholder="Location" value={form.location} onChange={update("location")} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <input type="number" min="1" max="5" placeholder="Rating 1-5" value={form.rating} onChange={update("rating")} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
          <textarea required placeholder="Patient quote" value={form.quote} onChange={update("quote")} rows={4} className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-sm" />
        </div>
        <div className="mt-6 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-5 py-2.5 rounded-full text-sm text-slate-700">Cancel</button>
          <button disabled={saving} data-testid="save-testimonial-btn" className="bg-green-700 text-white rounded-full px-6 py-2.5 text-sm font-medium disabled:opacity-60">{saving ? "Saving..." : "Add"}</button>
        </div>
      </form>
    </div>
  );
}
